// ============================================================================
// Service Desk — frontend service layer.
// Talks to the torre-de-control-api serviceDesk module. Two flavors:
//   • authed calls go through the shared `api` client (token + session headers)
//   • public calls (anonymous portal) go through a bare axios instance so the
//     session-expiry interceptor never kicks in for logged-out visitors.
// ============================================================================
import axios from "axios";
import api, { apiFormData } from "../../../utils/api";
import { getCanUseSG } from "../../../features/sg/access";
import { unwrap } from "./envelope";
import type { DevStage, DevStageId, DevStageLinks, StageOption } from "./devStages";
import type { EngineeringBridgeInfo } from "./engineeringBridge";
import type { AgentsPanel, CreatedAgent, ServiceDeskAgent } from "./agentsPanel";

// Tipos de los módulos puros, re-exportados para que las secciones sigan
// importando todo de aquí.
export type { DevStage, DevStageId, DevStageLinks, StageOption, StageStep, DevStageHistoryEntry } from "./devStages";
export type { EngineeringBridgeInfo, EngineeringBridgeState } from "./engineeringBridge";
export type { AgentsPanel, AgentPresence, CreatedAgent, ServiceDeskAgent } from "./agentsPanel";

function assertServiceDeskAccess(): void {
  if (!getCanUseSG()) {
    throw new Error("No tienes permisos para operar la Mesa de Servicio.");
  }
}

const API_URL =
  import.meta.env.VITE_FIREBASE_API_URL ||
  "http://127.0.0.1:5001/cc-console-development/us-central1/wayakDevelopmentApi";

const publicApi = axios.create({ baseURL: API_URL, timeout: 30000 });

// `unwrap` (./envelope) reaches the real payload through the platform's
// `{ meta, data }` + the serviceDesk controller's `{ message, data }` wrapping.
// Endpoints that use `pageResponse` (listTickets) are single-level and are
// handled inline below.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type SlaState = "met" | "on_time" | "at_risk" | "breached" | "unknown";

export interface FirestoreTimestamp {
  _seconds?: number;
  seconds?: number;
  _nanoseconds?: number;
  nanoseconds?: number;
}
export type TimestampLike = FirestoreTimestamp | string | number | null;

export interface BrandOption { id: string; label: string }
export interface CategoryOption { id: string; label: string }
export interface LabelOption { id: string; label: string; color?: string }
export interface StatusOption { id: string; label: string; color?: string; terminal?: boolean; pausesSla?: boolean }

export interface SlaTarget { firstResponseMins: number; resolutionMins: number }

// Look & feel for the agent workspace + public portal. All optional; empty
// values fall back to the tenant theme / built-in copy.
export interface Appearance {
  primaryColor?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  logoUrl?: string;
  portalIntro?: string;
}

export interface ServiceDeskSettings {
  statuses: StatusOption[];
  /** Configurable lifecycle graph: from-status id → allowed target status ids. */
  transitions?: Record<string, string[]>;
  /** Etapas del ciclo de desarrollo, con su nombre/color/uso por mesa (#559). */
  stages?: StageOption[];
  labels: LabelOption[];
  brands: BrandOption[];
  categories: CategoryOption[];
  priorities: TicketPriority[];
  slaTargets: Record<string, SlaTarget>;
  slaByBrand?: Record<string, Record<string, SlaTarget>>;
  slaRiskThreshold?: number;
  appearance?: Appearance;
  slaBreachNotifyEmails?: string[];
  aiAgent?: AiAgentSettings;
  /** Respuesta inmediata + conversación con IA de cara al cliente. */
  aiFirstReply?: AiFirstReplySettings;
  /**
   * Repos (owner/repo) a los que el PUENTE a ingeniería (#510) puede llevar un
   * ticket, además de GH_DEFAULT_REPO del servidor. Es la lista blanca que el
   * backend aplica: el selector del puente sólo ofrece éstos.
   */
  githubRepos?: string[];
}

/** Modelos permitidos (lista cerrada, igual que el backend). */
export type AiFirstReplyModel = "claude-haiku-4-5" | "claude-sonnet-5" | "claude-opus-5";

/**
 * Bloque `aiFirstReply` de los ajustes. Sólo un APROBADOR (correo probado con Google de
 * un dominio de SD_AI_STAFF_EMAIL_DOMAINS) puede guardarlo.
 */
export interface AiFirstReplySettings {
  enabled: boolean;
  persona: string;
  signature: string;
  model: AiFirstReplyModel;
  maxTokens: number;
  timeoutMs: number;
  excludedLabels: string[];
  excludedCategories: string[];
  excludedBrands: string[];
  conversationEnabled: boolean;
  conversationMaxTurns: number;
  conversationMaxCostUsd: number;
}

export interface PublicConfig {
  brands: BrandOption[];
  categories: CategoryOption[];
  labels: LabelOption[];
  priorities: TicketPriority[];
  appearance?: Appearance;
}

export interface Attachment { name: string; url: string; size?: number | null; contentType?: string | null }

export interface Comment {
  id: string;
  /** `ai_agent` = respuesta automática con IA (la escribe sólo el servidor). */
  authorType: "customer" | "agent" | "ai_agent";
  authorIsAi?: boolean;
  authorName: string;
  body: string;
  internal?: boolean;
  attachments?: Attachment[];
  createdAt?: TimestampLike;
}

export interface AuditEntry {
  id: string;
  actorName?: string;
  action: string;
  field?: string;
  from?: unknown;
  to?: unknown;
  createdAt?: TimestampLike;
}

export interface SlaStatus {
  overall: SlaState;
  firstResponse?: SlaState;
  resolution?: SlaState;
  paused?: boolean;
  dueAt?: { firstResponse?: number | null; resolution?: number | null };
  // --- #729: tiempos que no son del equipo. Sólo vista de agente. -----------
  /** Total en «Esperando al cliente», incluido el tramo en curso. */
  waitingCustomerMs?: number;
  /**
   * Resuelto → Cerrado. Mientras espera la confirmación es el tiempo que lleva
   * corriendo desde que se resolvió; null si nunca se resolvió.
   */
  closureMs?: number | null;
  confirmationHoldMs?: number;
}

/**
 * Dónde está el ticket respecto de su cierre (#729). La vista de cliente sólo
 * trae `pending` y `resolvedAt`; el resto (cuándo se desbloquea para la
 * persona asignada) es de la vista de agente. Fechas en epoch ms.
 */
export interface TicketClosure {
  /** Está en «Resuelto» esperando a que lo cierre quien lo abrió. */
  pending: boolean;
  resolvedAt: number | null;
  unlockAt?: number | null;
  unlocked?: boolean;
  lockDays?: number;
}

export interface TicketClosedBy {
  id: string | null;
  name: string | null;
  role: "requester" | "assignee";
}

/** Retrabajo: cuántas veces se dio por resuelto algo que no lo estaba. */
export interface TicketRework {
  count: number;
  // El detalle del último retrabajo sólo viaja en la vista de agente.
  lastAt?: TimestampLike;
  lastByName?: string | null;
  lastByRole?: "requester" | "staff";
  lastReason?: string | null;
}

/** Tickets resueltos que el solicitante aún no cierra; con `limit` o más no puede abrir otro. */
export interface PendingClosure {
  count: number;
  limit: number;
  blocked: boolean;
  tickets: { reference: string; title: string; resolvedAt?: TimestampLike; statusLabel?: string }[];
}

/**
 * Autonomous AI agent bookkeeping. Written only by the machine routes
 * (/serviceDesk/ai/*) that the local Claude runner talks to.
 *   idle → sin trabajar · claimed → el runner lo tiene tomado
 *   planned → triado, con issue de GitHub · pr-open → PR esperando revisión
 *   skipped → no es un problema de código · failed → falló el intento
 */
export type AiAgentState = "idle" | "claimed" | "planned" | "pr-open" | "skipped" | "failed";

export interface AiAgentInfo {
  state: AiAgentState;
  attempts?: number;
  claimedAt?: TimestampLike;
  leaseExpiresAt?: TimestampLike;
  /** Trust gate: set only by a staff member via approveAiAgent (audited). */
  approved?: boolean;
  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: TimestampLike;
  /** Hash of the content the approval covers; changing it revokes the approval. */
  approvedContentHash?: string | null;
  leaseId?: string | null;
  lastError?: string | null;
  planSummary?: string | null;
  issueUrl?: string | null;
  prUrl?: string | null;
  branch?: string | null;
  repo?: string | null;
  workedAt?: TimestampLike;
  // --- Respuesta inmediata con IA (la escribe el trigger del servidor) ------
  firstReplyAt?: TimestampLike;
  firstReplyState?: AiFirstReplyState;
  firstReplyCommentId?: string | null;
  firstReplyError?: string | null;
  /** "public" = sin cuenta / sin compañía (sólo acuse); "company" = con cuenta y compañía. */
  firstReplyScope?: "public" | "company" | null;
  /** Por qué salió el acuse y no texto del modelo. */
  firstReplyGuard?: string | null;
  firstReplyConfidence?: number | null;
  firstReplyIsCodeIssue?: boolean | null;
  firstReplyNeedsInfo?: boolean | null;
}

export type AiFirstReplyState = "idle" | "working" | "posting" | "sent" | "skipped" | "failed";

/** Bitácora de la conversación continua con IA (fuera de `aiAgent`). */
export interface AiConversationInfo {
  state?: "idle" | "working" | "handed_off" | "human_owns" | "failed";
  turns?: number;
  costUsd?: number;
  handedOffAt?: TimestampLike;
  handoffReason?: string | null;
  pausedByBot?: boolean;
  resumedAt?: TimestampLike;
}

/** Editable `aiAgent` block of the Service Desk settings document. */
export interface AiAgentSettings {
  enabled: boolean;
  maxAttempts: number;
  leaseMinutes: number;
  eligibleLabels: string[];
  eligibleCategories: string[];
  minPriority: TicketPriority | null;
  /**
   * When true (default) a ticket only reaches the agent after a staff member
   * approves it. Public-portal tickets always need approval regardless.
   */
  requireApproval: boolean;
}

export interface Ticket {
  id: string;
  reference: string;
  token?: string; // present in my-tickets + public create response
  brand: string;
  brandLabel?: string;
  title: string;
  description?: string;
  category?: string;
  module?: string;
  moduleSection?: string;
  priority: TicketPriority;
  status: string;
  statusLabel?: string;
  labels?: string[];
  requester?: { name: string | null; email: string | null; userId?: string | null };
  assignedAgentId?: string | null;
  assignedDevId?: string | null;
  escalated?: boolean;
  /** Sólo en la vista de agente: las vistas de cliente/token no traen el id interno. */
  companyId?: string | null;
  /**
   * Vista de agente: objeto completo. Vista de cliente/pública: sólo
   * `{ name, roleName }` (el backend no expone id ni correo del afectado).
   */
  affectedUser?: TicketAffectedUser | null;
  githubIssue?: { number?: number; htmlUrl?: string; url?: string; state?: string; repo?: string } | null;
  aiAgent?: AiAgentInfo | null;
  /** Puente a ingeniería (#510): trabajo en cola + issue de GitHub. Sólo vista de agente. */
  engineeringBridge?: EngineeringBridgeInfo | null;
  /**
   * Ciclo de desarrollo (#559). Vista de agente: con enlaces técnicos y
   * bitácora; vista de cliente: sólo etapa, fechas y línea de tiempo.
   */
  devStage?: DevStage | null;
  aiConversation?: AiConversationInfo | null;
  /** Marca «sin IA» de este ticket (la pone el staff). */
  aiFirstReplyDisabled?: boolean;
  /** "authenticated" | "public" (anonymous portal). */
  source?: string;
  sla?: { overall: SlaState; paused?: boolean };
  slaStatus?: SlaStatus;
  /** Estado del cierre por solicitante (#729). */
  closure?: TicketClosure | null;
  /** Quién lo cerró y en calidad de qué. Sólo vista de agente. */
  closedBy?: TicketClosedBy | null;
  rework?: TicketRework | null;
  slaBreachNotifiedDate?: string | null;
  firstResponseAt?: TimestampLike;
  resolvedAt?: TimestampLike;
  closedAt?: TimestampLike;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

export interface AssignableUser { id: string; name: string; email: string | null }

/** Usuario que sufre el problema — no siempre es quien abre el ticket. */
export interface AffectedUser {
  id: string;
  name: string;
  email: string | null;
  roleId: string | null;
  roleName: string | null;
}

/** Usuario afectado tal como viene en un ticket (parcial en vistas de cliente). */
export type TicketAffectedUser = Partial<Omit<AffectedUser, "name" | "roleName">> & {
  name: string | null;
  roleName: string | null;
};

/** Usuarios de una compañía, para el desplegable de «usuario afectado». */
export async function getCompanyUsers(companyId: string): Promise<AffectedUser[]> {
  if (!companyId) return [];
  const res = await api.get("/serviceDesk/company-users", { params: { companyId } });
  const list = unwrap(res);
  return Array.isArray(list) ? list : [];
}

export interface Metrics {
  totals: { all: number; open: number; closed: number };
  volumeByDay: { day: string; count: number }[];
  avgFirstResponseMins: number | null;
  avgResolutionMins: number | null;
  byCategory: { key: string; count: number }[];
  byLabel: { key: string; count: number }[];
  byBrand: { key: string; label: string; count: number }[];
  byPriority: { key: string; count: number }[];
  slaCompliancePct: number | null;
  slaCompliant: number;
  slaBreached: number;
  // #729 — opcionales: un backend anterior (dev desplegado vs. emulador) no los
  // manda y el dashboard no debe pintar "undefined".
  avgWaitingCustomerMins?: number | null;
  avgRequesterClosureMins?: number | null;
  pendingClosure?: number;
  pendingClosureUnlocked?: number;
  reworkedTickets?: number;
  reworkTotal?: number;
}

// ---------------------------------------------------------------------------
// PUBLIC (anonymous portal)
// ---------------------------------------------------------------------------
export async function getPublicConfig(): Promise<PublicConfig> {
  const res = await publicApi.get("/serviceDesk/public/config");
  return unwrap(res);
}

export interface CreatePublicTicketInput {
  name?: string;
  email: string;
  brand: string;
  title: string;
  description?: string;
  category?: string;
  module?: string;
  moduleSection?: string;
  priority?: TicketPriority;
  files?: File[];
  /**
   * El portal anónimo NO envía compañía ni usuario afectado: el backend los
   * ignora (un anónimo no puede demostrar pertenencia) y el agente los asigna
   * al triar. Sólo se usan en `createMyTicket`.
   */
  companyId?: string;
  affectedUserId?: string;
  deviceImeis?: string[];
}

/** Alta autenticada: compañía y usuario afectado obligatorios. */
export interface CreateMyTicketInput extends Omit<CreatePublicTicketInput, "email" | "companyId" | "affectedUserId"> {
  email?: string;
  /**
   * Compañía del ticket. Sólo cuenta para admin reseller / agentes de mesa: al
   * resto el backend los fija a su propia compañía.
   */
  companyId: string;
  /** Usuario de esa compañía al que le ocurre el problema. */
  affectedUserId: string;
}

export interface CreateTicketResult {
  reference: string;
  token: string;
  statusUrl: string;
  ticket: Ticket;
}

export async function createPublicTicket(input: CreatePublicTicketInput): Promise<CreateTicketResult> {
  const form = new FormData();
  form.append("name", input.name || "");
  form.append("email", input.email);
  form.append("brand", input.brand);
  form.append("title", input.title);
  form.append("description", input.description || "");
  if (input.category) form.append("category", input.category);
  if (input.module) form.append("module", input.module);
  if (input.moduleSection) form.append("moduleSection", input.moduleSection);
  if (input.priority) form.append("priority", input.priority);
  // companyId / affectedUserId no se envían: el alta anónima los ignora.
  if (input.deviceImeis && input.deviceImeis.length > 0) {
    input.deviceImeis.forEach((imei, i) => form.append(`deviceImeis[${i}]`, imei));
  }
  (input.files || []).forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await publicApi.post("/serviceDesk/public/tickets", form);
  return unwrap(res);
}

export async function getPublicTicket(ref: string): Promise<{ ticket: Ticket; comments: Comment[] }> {
  const res = await api.get("/serviceDesk/tickets/public", { params: { ref } });
  return unwrap(res);
}

export async function addPublicComment(
  ref: string,
  body: string,
  files: File[] = [],
): Promise<Comment> {
  const form = new FormData();
  form.append("body", body);
  files.forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await api.post("/serviceDesk/tickets/public/comments", form, {
    params: { ref },
  });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// AUTHED — customer
// ---------------------------------------------------------------------------
export async function createMyTicket(input: CreateMyTicketInput): Promise<Ticket> {
  // Sin esto FormData mandaría el literal "undefined" y el backend respondería
  // con un error engañoso: mejor fallar aquí con un mensaje claro.
  if (!input.companyId || !input.affectedUserId) {
    throw new Error("companyId y affectedUserId son obligatorios");
  }
  const form = new FormData();
  form.append("brand", input.brand);
  form.append("title", input.title);
  form.append("description", input.description || "");
  if (input.category) form.append("category", input.category);
  if (input.module) form.append("module", input.module);
  if (input.moduleSection) form.append("moduleSection", input.moduleSection);
  if (input.priority) form.append("priority", input.priority);
  if (input.email) form.append("requesterEmail", input.email);
  if (input.name) form.append("requesterName", input.name);
  form.append("companyId", input.companyId);
  form.append("affectedUserId", input.affectedUserId);
  (input.files || []).forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await apiFormData.post("/serviceDesk/tickets", form);
  return unwrap(res);
}

export async function getMyTickets(): Promise<Ticket[]> {
  const res = await api.get("/serviceDesk/tickets/mine");
  return unwrap(res) || [];
}

/**
 * Tickets en «Resuelto» que el usuario abrió y no ha cerrado (#729). Con
 * `blocked` el backend rechaza el alta (409 SD_PENDING_CLOSURE_LIMIT), así que
 * el formulario consulta esto antes para no dejarle llenar algo que no va a
 * poder enviar.
 */
export async function getPendingClosure(): Promise<PendingClosure> {
  const res = await api.get("/serviceDesk/tickets/pending-closure");
  const data = unwrap(res) || {};
  return {
    count: Number(data.count) || 0,
    limit: Number(data.limit) || 0,
    blocked: data.blocked === true,
    tickets: Array.isArray(data.tickets) ? data.tickets : [],
  };
}

/** El solicitante cierra su propio ticket: es la confirmación de que quedó resuelto. */
export async function closeMyTicket(ref: string): Promise<Ticket> {
  const res = await api.post("/serviceDesk/tickets/public/close", undefined, { params: { ref } });
  return unwrap(res);
}

/**
 * El solicitante dice que NO quedó resuelto: el ticket pasa de «Resuelto» a
 * «Rehacer» y suma al contador de retrabajos. El backend exige el motivo
 * (mínimo 5 caracteres) y sólo lo acepta mientras está en «Resuelto».
 */
export async function requestTicketRework(ref: string, reason: string): Promise<Ticket> {
  const res = await api.post("/serviceDesk/tickets/public/rework", { reason }, { params: { ref } });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// AUTHED — agent
// ---------------------------------------------------------------------------
export interface ListFilters {
  scope?: "open" | "closed" | "all";
  brand?: string;
  status?: string;
  priority?: string;
  label?: string;
  agentId?: string;
  sla?: SlaState;
  q?: string;
  page?: number;
  limit?: number;
}

export async function listTickets(filters: ListFilters = {}): Promise<{ data: Ticket[]; totalItems: number; totalPages: number; currentPage: number }> {
  const res = await api.get("/serviceDesk/tickets", { params: filters });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? 1,
  };
}

export async function getTicket(id: string): Promise<{ ticket: Ticket; comments: Comment[]; audit: AuditEntry[]; attachments?: Attachment[] }> {
  const res = await api.get(`/serviceDesk/tickets/${id}`);
  return unwrap(res);
}

export async function updateTicket(id: string, patch: Partial<{
  status: string;
  priority: TicketPriority;
  labels: string[];
  category: string;
  module: string;
  moduleSection: string;
  brand: string;
  assignedAgentId: string | null;
  assignedDevId: string | null;
  escalated: boolean;
  companyId: string | null;
  affectedUserId: string | null;
  description?: string;
  aiFirstReplyDisabled: boolean;
  /** Reactivar la conversación con IA (sólo un aprobador). */
  aiConversationResume: boolean;
}>): Promise<Ticket> {
  assertServiceDeskAccess();
  const res = await api.put(`/serviceDesk/tickets/${id}`, patch);
  return unwrap(res);
}

export async function deleteTicket(id: string): Promise<void> {
  assertServiceDeskAccess();
  await api.delete(`/serviceDesk/tickets/${id}`);
}

export async function addAgentComment(id: string, body: string, internal: boolean, files: File[] = []): Promise<Comment> {
  assertServiceDeskAccess();
  const form = new FormData();
  form.append("body", body);
  form.append("internal", String(internal));
  files.forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await apiFormData.post(`/serviceDesk/tickets/${id}/comments`, form);
  return unwrap(res);
}

export async function linkGithubIssue(id: string, input: { repo?: string; title?: string; body?: string; labels?: string[] }): Promise<Ticket> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/github`, input);
  return unwrap(res);
}

/** Link an EXISTING GitHub issue by pasting its URL (or "owner/repo#123"). */
export async function linkExistingGithubIssue(id: string, url: string): Promise<Ticket> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/github/link`, { url });
  return unwrap(res);
}

/**
 * Devuelve el ticket a la cola del agente IA (botón "Reintentar" del panel).
 * `refund` también devuelve el intento consumido. Si el runner ya lo había
 * movido a "En progreso" (PR abierto), el backend lo regresa a "Abierto".
 * La respuesta es la proyección del runner (parcial): recarga el ticket.
 */
export async function releaseAiAgent(id: string, refund = true): Promise<Partial<Ticket>> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/release`, { refund });
  return unwrap(res);
}

/** Resultado de aprobar/retirar la aprobación del agente IA. */
export interface AiApprovalResult {
  id: string;
  aiAgent: AiAgentInfo;
  /** null = el ticket ya es elegible; si no, el motivo por el que aún no. */
  eligibility: string | null;
  /** Estado del puente a ingeniería tras aprobar/retirar/reintentar (#510). */
  engineeringBridge?: EngineeringBridgeInfo | null;
}

/**
 * Reintenta el puente a ingeniería de un ticket aprobado (#510): trabajo en la
 * cola del runner + issue de GitHub. `repo` sólo hace falta cuando el backend
 * no pudo resolverlo (`reason: repo_not_resolved`); el backend lo valida
 * contra la lista de repos de la mesa.
 */
export async function retryServiceDeskBridge(
  id: string,
  input: { repo?: string } = {},
): Promise<{ id: string; status?: string; aiAgent?: AiAgentInfo; engineeringBridge?: EngineeringBridgeInfo | null; githubIssue?: Ticket["githubIssue"] }> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/bridge`, input.repo ? { repo: input.repo } : {});
  return unwrap(res);
}

/**
 * Mueve la etapa del ticket (#559). La línea de tiempo la ve el CLIENTE, así
 * que es acción de staff; a diferencia de las transiciones automáticas, el
 * staff SÍ puede retroceder (corregir lo que una máquina dejó mal).
 */
export async function setTicketStage(
  id: string,
  input: { stage: DevStageId; note?: string; links?: DevStageLinks },
): Promise<{ id: string; devStage: DevStage }> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/stage`, input);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Agentes de código — registro y presencia (#511)
// ---------------------------------------------------------------------------
export async function listServiceDeskAgents(): Promise<AgentsPanel> {
  assertServiceDeskAccess();
  const res = await api.get("/serviceDesk/agents");
  return unwrap(res);
}

/**
 * Da de alta un agente. La llave en claro viene en `key` y es la ÚNICA vez que
 * existe fuera del runner: el backend sólo guarda su sha256.
 */
export async function createServiceDeskAgent(input: {
  name: string;
  host?: string;
  version?: string;
}): Promise<CreatedAgent> {
  assertServiceDeskAccess();
  const res = await api.post("/serviceDesk/agents", input);
  return unwrap(res);
}

export async function renameServiceDeskAgent(id: string, name: string): Promise<ServiceDeskAgent> {
  assertServiceDeskAccess();
  const res = await api.put(`/serviceDesk/agents/${id}`, { name });
  return unwrap(res);
}

/** Revocar es inmediato: la siguiente llamada del runner con esa llave es 401. */
export async function revokeServiceDeskAgent(id: string): Promise<ServiceDeskAgent> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/agents/${id}/revoke`, {});
  return unwrap(res);
}

/** Compuerta de confianza: autoriza al agente IA a trabajar este ticket. */
export async function approveAiAgent(id: string): Promise<AiApprovalResult> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/approve`, {});
  return unwrap(res);
}

/** Retira la aprobación: el ticket deja de ser elegible para el agente IA. */
export async function revokeAiAgentApproval(id: string): Promise<AiApprovalResult> {
  assertServiceDeskAccess();
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/revoke`, {});
  return unwrap(res);
}

export async function getSettings(): Promise<ServiceDeskSettings> {
  const res = await api.get("/serviceDesk/settings");
  return unwrap(res);
}

export async function updateSettings(patch: Partial<ServiceDeskSettings>): Promise<ServiceDeskSettings> {
  assertServiceDeskAccess();
  const res = await api.put("/serviceDesk/settings", patch);
  return unwrap(res);
}

export async function getAssignableUsers(): Promise<AssignableUser[]> {
  const res = await api.get("/serviceDesk/assignable-users");
  return unwrap(res) || [];
}

export interface GithubRepository {
  id: number;
  name: string;
  fullName: string;
  url: string;
  owner: string;
  isPrivate: boolean;
}

export async function getGithubRepositories(): Promise<GithubRepository[]> {
  const res = await api.get("/serviceDesk/github/repositories");
  return unwrap(res) || [];
}

export async function getMetrics(params: { brand?: string; from?: string; to?: string } = {}): Promise<Metrics> {
  const res = await api.get("/serviceDesk/metrics", { params });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function tsToDate(value: TimestampLike | undefined): Date | null {
  if (!value) return null;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);
  return null;
}

export function formatDate(value: TimestampLike | undefined): string {
  const d = tsToDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

export const SLA_META: Record<SlaState, { label: string; color: string; bg: string }> = {
  met: { label: "Cumplido", color: "#059669", bg: "#d1fae5" },
  on_time: { label: "En tiempo", color: "#059669", bg: "#d1fae5" },
  at_risk: { label: "En riesgo", color: "#b45309", bg: "#fef3c7" },
  breached: { label: "Incumplido", color: "#dc2626", bg: "#fee2e2" },
  unknown: { label: "—", color: "#6b7280", bg: "#f3f4f6" },
};

export const AI_AGENT_META: Record<AiAgentState, { label: string; color: string; bg: string }> = {
  idle: { label: "Sin trabajar", color: "#6b7280", bg: "#f3f4f6" },
  claimed: { label: "Trabajando", color: "#7c3aed", bg: "#ede9fe" },
  planned: { label: "Planificado", color: "#2563eb", bg: "#dbeafe" },
  "pr-open": { label: "PR abierto", color: "#059669", bg: "#d1fae5" },
  skipped: { label: "No aplica", color: "#6b7280", bg: "#f3f4f6" },
  failed: { label: "Falló", color: "#dc2626", bg: "#fee2e2" },
};

export const PRIORITY_META: Record<TicketPriority, { label: string; color: string }> = {
  low: { label: "Baja", color: "#6b7280" },
  medium: { label: "Media", color: "#2563eb" },
  high: { label: "Alta", color: "#d97706" },
  urgent: { label: "Urgente", color: "#dc2626" },
};

// Status ids reachable from `current` per the configured transition graph,
// always including the current status itself (a no-op move). When no graph is
// configured every status is selectable.
export function allowedNextStatuses(settings: ServiceDeskSettings | null, current: string): string[] {
  const all = (settings?.statuses || []).map((s) => s.id);
  const graph = settings?.transitions;
  if (!graph || !graph[current]) return all;
  return all.filter((id) => id === current || graph[current].includes(id));
}

export function formatMins(mins: number | null): string {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} h ${m} min` : `${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} d ${rh} h` : `${d} d`;
}
